import React, { FC, useState } from 'react';
import { DEF_PG_SIZE_SUB } from '../../constants/maximum-page-size';
import { useLocale } from "../../contexts/localeContext";
import { TableContextProvider } from '../../contexts/table/TableContextProvider';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import DeviceScheduleColumn from '../../models/ColumnDefinitions/DeviceScheduleColumn';
import { ListData } from '../../models/ListData';
import { ListQuery } from '../../models/ListQuery';
import DeviceScheduleSearch from '../../models/search/DeviceScheduleSearch';
import { getDeviceSchedulePresetById, getPresetDeviceSchedules, storeDeviceSchedulePreset } from '../../services/presetDeviceSchedule';
import { deletePresetSetting } from '../../services/presetService';
import { getErrorMessage, showLoadItemError } from '../../services/utilities/ServiceError';
import ActivityFrame from '../activity-frame/activity-frame';
import { useDialog } from '../dialog/DialogContext';
import TableTab from "../tabs/table-tab";
import { toast } from "react-toastify";
import { PresetDeviceScheduleWizard } from './presetDeviceScheduleWizard/presetDeviceScheduleWizard';
import {useFormikContext} from "formik";

interface Props {
    itemId: string;
    onDelete: any,
    onSave: (createNew) => void;
    onDeleteUpdateBadge: (update) => void;
    profileType: string;
}

const PresetsDeviceSchedules: FC<Props> = ({ itemId, onDelete, onSave, profileType, onDeleteUpdateBadge }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.assignedSchedules);
    const [showModal, setShowModal] = useState(false);
    const [busy, setBusy] = useState(false);
    const { values } = useFormikContext<any>();

    const initialValues = {
        columnId: '',
        settingIdToCopy: '',
        profileId: itemId,
        defaultModeId: '',
        deviceTimePatternId: '',
    };
    const [currentItem, setCurrentItem] = useState<any>(initialValues);
    const dialog = useDialog();

    const delay = ms => new Promise(
        resolve => setTimeout(resolve, ms)
      );

    const getAllScheduledPresets = async (listParameters) => {
       
        await delay(100);
        return getPresetDeviceSchedules(itemId, translate, listParameters);
    }

    const handleSave = (deviceSchedule) => {
        setBusy(true);
        const saveNew = currentItem.id ? false : true

        if (saveNew == false) {
            deletePresetSetting(currentItem.id)
                .then(
                    deleteResult => {
                        onDeleteUpdateBadge(true);
                        storeDeviceSchedulePreset(deviceSchedule)
                            .then(success => {
                                toast.success(translate.byKey(TranslationKeys.deviceScheduleSaved));
                                onSave(true);
                            })
                            .catch(failure => {
                                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) })
                            })
                            .finally(() => {
                                setBusy(false);
                                dataRefresh((query) => getAllScheduledPresets(query));
                            });
                    })
        }
        else {
            storeDeviceSchedulePreset(deviceSchedule)
                .then(success => {
                    toast.success(translate.byKey(TranslationKeys.deviceScheduleSaved));
                    onSave(saveNew)
                })
                .catch(failure => {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) })
                })
                .finally(() => {
                    setBusy(false);
                    dataRefresh((query) => getAllScheduledPresets(query));
                });
        }
    }

    const editDeviceSchedule = async (id: string) => {
        try {
            const preset = await getDeviceSchedulePresetById(id);
            setCurrentItem(preset);
            setShowModal(true);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { setShowModal(false); });
        }
    }

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    return <>
        <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
            <TableContextProvider
                dataMethod={(query) => getAllScheduledPresets(query)}
                manualDataRefresh={manualDataRefresh}
                tableDescriptor={DeviceScheduleColumn}
                searchDescriptor={DeviceScheduleSearch}
                deleteMethod={onDelete}
                editMethod={(id) => editDeviceSchedule(id)}
                useTable
                useSearch
                usePagination
                maxPageSize={DEF_PG_SIZE_SUB}>
                <TableTab
                    title={title}
                    selectRows={false}
                    cyName='presetDeviceSchedule'>
                    <button type="button" className="flex px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                        onClick={() => { setShowModal(true) }}>
                        {translate.byKey(TranslationKeys.add)}
                    </button>
                    <PresetDeviceScheduleWizard
                        isOpen={showModal}
                        onClose={() => {
                            setCurrentItem(initialValues);
                            setShowModal(false);
                        }}
                        title={currentItem.id ? translate.byKey(TranslationKeys.editSchedule) : translate.byKey(TranslationKeys.addSchedule)}
                        currentItem={currentItem}
                        profileType={profileType}
                        presetType={values.pSysnameType}
                        onSave={handleSave}>
                    </PresetDeviceScheduleWizard>
                </TableTab>
            </TableContextProvider>
        </ActivityFrame>
    </>
};

export default PresetsDeviceSchedules;


